import { DateRangePicker } from '@/components/filters/DateRangePicker'
import { FilterBar, FilterField } from '@/components/filters/FilterBar'
import { MultiSelect } from '@/components/filters/MultiSelect'
import { Input } from '@/components/ui/input'
import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { hrBreakdownQuery, hrFilters, mapBreakdown } from '@/services/cube/hr'
import { FILTER_KEYS, useHrFilterState, useHrScope } from '@/features/hr/use-hr-list'

const t = messages.hr

interface OptionFilter {
  key: 'durum' | 'kategori' | 'kaynak'
  label: string
  options: { value: string; label: string }[]
}

/**
 * Firma, şube, seçenekli filtreler, arama ve (verilirse) tarih aralığı. Şube seçenekleri
 * seçili firmaların şubeleri (Cube'dan).
 */
export function HrFilterBar({
  view,
  idPrefix,
  optionFilters = [],
  withDates = false,
  extraKeys = [],
}: {
  view: string
  idPrefix: string
  optionFilters?: OptionFilter[]
  withDates?: boolean
  extraKeys?: string[]
}) {
  const state = useHrFilterState()
  const scope = useHrScope()
  const firms = scope.data?.firms ?? scope.data?.all_firms ?? []
  const firmFilter = hrFilters({ view, equals: { firma: state.firma } })
  const branches = useCubeLiveQuery({
    queryKey: queryKeys.cube(view, { kind: 'branches', firma: state.firma }),
    query: hrBreakdownQuery(view, ['sube'], [], firmFilter),
    select: mapBreakdown(view, ['sube'], []),
    subscribe: false,
  })
  const branchOptions = (branches.data ?? [])
    .map((row) => row.sube as string | null)
    .filter((value): value is string => !!value)
    .map((value) => ({ value, label: value }))

  return (
    <FilterBar onReset={() => state.query.reset([...FILTER_KEYS, ...extraKeys])}>
      <MultiSelect
        label={t.filters.firm}
        options={firms.map((value) => ({ value, label: value }))}
        selected={state.firma}
        onChange={(values) => state.query.set({ firma: values, sube: undefined })}
        withApply
      />
      <MultiSelect
        label={t.filters.branch}
        options={branchOptions}
        selected={state.sube}
        onChange={(values) => state.query.set({ sube: values })}
        withApply
      />
      {optionFilters.map((filter) => (
        <MultiSelect
          key={filter.key}
          label={filter.label}
          options={filter.options}
          selected={state[filter.key]}
          onChange={(values) => state.query.set({ [filter.key]: values })}
        />
      ))}
      {withDates && (
        <DateRangePicker
          idPrefix={idPrefix}
          from={state.from}
          to={state.to}
          onChange={({ from, to }) => state.query.set({ from, to })}
        />
      )}
      <FilterField label={t.filters.search} htmlFor={`${idPrefix}-q`}>
        <Input
          key={state.q ?? ''}
          id={`${idPrefix}-q`}
          className="w-52"
          placeholder={t.filters.searchPlaceholder}
          defaultValue={state.q ?? ''}
          onBlur={(event) => state.query.set({ q: event.target.value || undefined })}
          onKeyDown={(event) => {
            if (event.key === 'Enter')
              state.query.set({ q: event.currentTarget.value || undefined })
          }}
        />
      </FilterField>
    </FilterBar>
  )
}
